import { useState } from 'react';

import type { PendingClaimItem } from '@/lib/api/clubsApi';
import { formatDateAbbreviatedFr } from '@shared';
import { AlertTriangle, Check, X } from 'lucide-react';

interface PendingClaimsPanelProps {
  claims: PendingClaimItem[];
  isLoading: boolean;
  onApprove: (claimId: number) => void;
  onReject: (claimId: number, reason?: string) => void;
  approvePending: boolean;
  rejectPending: boolean;
}

interface RejectDialogState {
  claimId: number;
  clubName: string;
  requesterName: string;
}

export function PendingClaimsPanel({
  claims,
  isLoading,
  onApprove,
  onReject,
  approvePending,
  rejectPending,
}: PendingClaimsPanelProps) {
  const [approveTarget, setApproveTarget] = useState<PendingClaimItem | null>(null);
  const [rejectDialog, setRejectDialog] = useState<RejectDialogState | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  if (!isLoading && claims.length === 0) return null;

  return (
    <>
      <div className='mb-8'>
        <div className='mb-3 flex items-center gap-2'>
          <AlertTriangle className='h-4 w-4 text-amber-500' />
          <h2 className='text-sm font-semibold text-slate-700'>Demandes admin club en attente</h2>
          {!isLoading && (
            <span className='rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700'>
              {claims.length}
            </span>
          )}
        </div>

        <div className='rounded-xl border border-amber-200 bg-white'>
          <div className='overflow-x-auto'>
            <table className='w-full text-sm'>
              <thead className='border-b border-amber-200 bg-amber-50'>
                <tr>
                  <th className='px-5 py-3 text-left text-xs font-semibold uppercase text-slate-500'>Club</th>
                  <th className='px-5 py-3 text-left text-xs font-semibold uppercase text-slate-500'>Demandeur</th>
                  <th className='px-5 py-3 text-left text-xs font-semibold uppercase text-slate-500'>Votes</th>
                  <th className='px-5 py-3 text-left text-xs font-semibold uppercase text-slate-500'>Date</th>
                  <th className='px-5 py-3 text-right text-xs font-semibold uppercase text-slate-500'>Actions</th>
                </tr>
              </thead>
              <tbody className='divide-y divide-slate-100'>
                {isLoading
                  ? Array.from({ length: 3 }).map((_, i) => (
                      <tr key={i} className='animate-pulse'>
                        <td className='px-5 py-4'>
                          <div className='h-4 w-40 rounded-full bg-slate-100' />
                        </td>
                        <td className='px-5 py-4'>
                          <div className='h-4 w-48 rounded-full bg-slate-100' />
                        </td>
                        <td className='px-5 py-4'>
                          <div className='h-4 w-10 rounded-full bg-slate-100' />
                        </td>
                        <td className='px-5 py-4'>
                          <div className='h-4 w-24 rounded-full bg-slate-100' />
                        </td>
                        <td className='px-5 py-4' />
                      </tr>
                    ))
                  : claims.map(claim => (
                      <tr key={claim.id} className='transition-colors hover:bg-slate-50'>
                        <td className='px-5 py-3.5'>
                          <p className='font-semibold text-slate-900'>{claim.club.name}</p>
                          {claim.club.alias && <p className='font-mono text-xs text-slate-400'>#{claim.club.alias}</p>}
                          <p className='text-xs text-slate-400'>{claim.club.city}</p>
                        </td>
                        <td className='px-5 py-3.5'>
                          <p className='text-slate-700'>
                            {claim.requester.firstName} {claim.requester.lastName}
                          </p>
                          <p className='text-xs text-slate-400'>{claim.requester.email}</p>
                          {claim.requester.licenseNumber && (
                            <p className='font-mono text-xs text-slate-400'>
                              Licence : {claim.requester.licenseNumber}
                            </p>
                          )}
                        </td>
                        <td className='px-5 py-3.5'>
                          <span className='inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700'>
                            {claim.voteCount} vote{claim.voteCount > 1 ? 's' : ''}
                          </span>
                        </td>
                        <td className='whitespace-nowrap px-5 py-3.5 text-xs text-slate-500'>
                          {formatDateAbbreviatedFr(String(claim.createdAt))}
                        </td>
                        <td className='px-5 py-3.5'>
                          <div className='flex items-center justify-end gap-2'>
                            <button
                              onClick={() => setApproveTarget(claim)}
                              disabled={approvePending}
                              className='flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-emerald-700 disabled:opacity-50'
                            >
                              <Check className='h-3.5 w-3.5' />
                              Approuver
                            </button>
                            <button
                              onClick={() =>
                                setRejectDialog({
                                  claimId: claim.id,
                                  clubName: claim.club.name,
                                  requesterName: `${claim.requester.firstName} ${claim.requester.lastName}`,
                                })
                              }
                              disabled={rejectPending}
                              className='flex items-center gap-1.5 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50'
                            >
                              <X className='h-3.5 w-3.5' />
                              Rejeter
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Approve confirmation dialog */}
      {approveTarget !== null && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40'>
          <div className='mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-xl'>
            <div className='mb-4 flex items-start gap-3'>
              <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100'>
                <Check className='h-5 w-5 text-emerald-600' />
              </div>
              <div>
                <h3 className='text-base font-semibold text-slate-900'>Approuver la demande</h3>
                <p className='mt-1 text-sm text-slate-500'>
                  Confirmer l&apos;approbation de{' '}
                  <span className='font-medium text-slate-700'>
                    {approveTarget.requester.firstName} {approveTarget.requester.lastName}
                  </span>{' '}
                  comme administrateur du club{' '}
                  <span className='font-medium text-slate-700'>{approveTarget.club.name}</span> ?
                </p>
              </div>
            </div>
            <div className='flex justify-end gap-2'>
              <button
                onClick={() => setApproveTarget(null)}
                disabled={approvePending}
                className='rounded-lg border border-slate-200 px-4 py-2 text-sm transition hover:bg-slate-50 disabled:opacity-50'
              >
                Annuler
              </button>
              <button
                onClick={() => {
                  onApprove(approveTarget.id);
                  setApproveTarget(null);
                }}
                disabled={approvePending}
                className='rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:opacity-50'
              >
                {approvePending ? 'En cours…' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject dialog */}
      {rejectDialog !== null && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40'>
          <div className='mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-xl'>
            <div className='mb-4 flex items-start gap-3'>
              <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-100'>
                <X className='h-5 w-5 text-red-600' />
              </div>
              <div>
                <h3 className='text-base font-semibold text-slate-900'>Rejeter la demande</h3>
                <p className='mt-1 text-sm text-slate-500'>
                  Rejeter la demande de <span className='font-medium text-slate-700'>{rejectDialog.requesterName}</span>{' '}
                  pour le club <span className='font-medium text-slate-700'>{rejectDialog.clubName}</span>.
                </p>
              </div>
            </div>
            <div className='mb-4'>
              <label htmlFor='reject-reason' className='mb-1.5 block text-xs font-medium text-slate-600'>
                Motif de rejet (optionnel)
              </label>
              <textarea
                id='reject-reason'
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder='Expliquer la raison du rejet…'
                rows={3}
                className='w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none'
              />
            </div>
            <div className='flex justify-end gap-2'>
              <button
                onClick={() => {
                  setRejectDialog(null);
                  setRejectReason('');
                }}
                disabled={rejectPending}
                className='rounded-lg border border-slate-200 px-4 py-2 text-sm transition hover:bg-slate-50 disabled:opacity-50'
              >
                Annuler
              </button>
              <button
                onClick={() => {
                  onReject(rejectDialog.claimId, rejectReason.trim() || undefined);
                  setRejectDialog(null);
                  setRejectReason('');
                }}
                disabled={rejectPending}
                className='rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:opacity-50'
              >
                {rejectPending ? 'En cours…' : 'Rejeter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
